import type { AgeTier, CreateStudentPayload } from '../api/backend/members'
import { STUDENT_MASCOTS, STUDENT_SECRET_EMOJIS, buildStudentPassword, buildStudentUsername, normalizeStudentFullName } from './robolabx-login-catalog'

export interface AddStudentWizardDraft {
  givenName: string
  surName: string
  ageTier: AgeTier | ''
  mascotId: string | null
  secretIds: string[]
}

export function buildAddStudentFullName(givenName: string, surName: string): string {
  return normalizeStudentFullName([givenName.trim(), surName.trim()].filter(Boolean).join(' '))
}

export function canContinueAddStudentBasics(draft: Pick<AddStudentWizardDraft, 'givenName' | 'surName' | 'ageTier'>): boolean {
  return Boolean(draft.givenName.trim() && draft.surName.trim() && draft.ageTier)
}

export function canSubmitAddStudent(draft: AddStudentWizardDraft): boolean {
  return canContinueAddStudentBasics(draft) && Boolean(draft.mascotId) && draft.secretIds.length === 3
}

export function appendStudentSecretId(secretIds: string[], secretId: string): string[] {
  if (secretIds.length >= 3) return secretIds
  return [...secretIds, secretId]
}

export function clearStudentSecretIds(): string[] {
  return []
}

export function generateRandomStudentSecretIds(count = 3, random = Math.random): string[] {
  const available = [...STUDENT_SECRET_EMOJIS.map((item) => item.id)]

  for (let index = available.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1))
    ;[available[index], available[swapIndex]] = [available[swapIndex], available[index]]
  }

  return available.slice(0, Math.max(0, Math.min(count, available.length)))
}

export function renderStudentSecretEmojis(secretIds: string[]): string {
  return secretIds
    .map((secretId) => STUDENT_SECRET_EMOJIS.find((item) => item.id === secretId)?.emoji ?? secretId)
    .join(' ')
}

export function resolveStudentUsernameSuffix(currentUsername: string): string {
  const normalized = currentUsername.trim()
  if (!normalized) return ''
  const segments = normalized.split('|').map((segment) => segment.trim()).filter(Boolean)
  if (segments.length <= 1) return normalized
  return segments.slice(1).join('|')
}

export interface ParsedStudentUsername {
  raw: string
  mascotId: string | null
  fullName: string | null
}

export function parseStudentUsername(username: string | null | undefined): ParsedStudentUsername {
  const raw = username?.trim() ?? ''
  if (!raw) {
    return { raw: '', mascotId: null, fullName: null }
  }

  const segments = raw.split('|').map((segment) => segment.trim()).filter(Boolean)
  if (segments.length >= 2 && STUDENT_MASCOTS.some((mascot) => mascot.id === segments[0])) {
    return {
      raw,
      mascotId: segments[0],
      fullName: segments.slice(1).join('|') || null,
    }
  }

  return {
    raw,
    mascotId: null,
    fullName: null,
  }
}

export function buildStudentUsernameForMascotChange(mascotId: string, currentUsername: string): string {
  const parsed = parseStudentUsername(currentUsername)
  const suffix = parsed.fullName ?? resolveStudentUsernameSuffix(currentUsername)
  return suffix ? `${mascotId}|${suffix}` : mascotId
}

export function buildCreateStudentPayloadFromWizard(draft: AddStudentWizardDraft): CreateStudentPayload {
  if (!canContinueAddStudentBasics(draft)) {
    throw new Error('First Name, Last Name and Age Tier are required.')
  }
  if (!draft.mascotId) {
    throw new Error('Mascot is required.')
  }
  if (draft.secretIds.length !== 3) {
    throw new Error('Picture secret must contain exactly 3 emoji.')
  }

  const givenName = draft.givenName.trim()
  const surName = draft.surName.trim()
  const fullName = buildAddStudentFullName(givenName, surName)

  return {
    givenName,
    surName,
    ageTier: draft.ageTier as AgeTier,
    username: buildStudentUsername(draft.mascotId, fullName),
    initialPassword: buildStudentPassword(draft.secretIds),
  }
}
